// Auto-extracted from SellerProducts.jsx
import { useState, useEffect, useMemo } from "react";
import { C } from "../../lib/theme";
import { Card } from "../../components/Card";
import { Btn } from "../../components/Btn";
import { Eyebrow } from "../../components/Eyebrow";
import { Icon } from "../../components/Icon";
import { Skeleton } from "../../components/Skeleton";
import { EmptyState } from "../../components/EmptyState";
import { supabase } from "../../lib/supabase";
import { fmtK } from "../../lib/money";
import { gradientForId } from "../../lib/gradients";
import { resolveImage } from "../../lib/images";
import { useCategories } from "../../hooks/useCategories";
import { STATUS_TABS, PRESET_VARIANT_TYPES, DELIVERY_METHODS, MAX_PHOTOS } from "./constants";
import { FieldLabel, Field } from "./formHelpers";

export function VariantEditor({
  variants,
  setVariants,
  showTypePicker,
  setShowTypePicker,
  editingVariant,
  setEditingVariant,
  variantCustomInput,
  setVariantCustomInput,
}) {
  function removeOption(idx, opt) {
    setVariants(
      variants.map((v, i) => (i === idx ? { ...v, options: v.options.filter((o) => o !== opt) } : v))
    );
  }
  function addOption(idx, opt) {
    const val = typeof opt === "string" ? opt.trim() : "";
    if (!val) return;
    if (variants[idx].options.includes(val)) return;
    setVariants(variants.map((v, i) => (i === idx ? { ...v, options: [...v.options, val] } : v)));
  }

  return (
    <div>
      {variants.map((variant, idx) => {
        const preset = PRESET_VARIANT_TYPES.find((t) => t.label === variant.type);
        const unselected = (preset?.presets || []).filter((p) => !variant.options.includes(p));
        const editing = editingVariant === idx;
        return (
          <div
            key={idx}
            style={{
              background: C.cream,
              borderRadius: 14,
              padding: 12,
              marginBottom: 10,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 800, color: C.charcoal }}>
                {preset?.icon || "✏️"} {variant.type}
              </div>
              <button
                onClick={() => setVariants(variants.filter((_, i) => i !== idx))}
                style={{ background: "transparent", border: "none", cursor: "pointer", padding: 2 }}
                title="Remove variant type"
              >
                <Icon name="x" size={14} color={C.muted} />
              </button>
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: editing ? 8 : 0 }}>
              {variant.options.map((opt) => (
                <button
                  key={opt}
                  onClick={() => removeOption(idx, opt)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    padding: "6px 10px",
                    borderRadius: 20,
                    background: C.hunter,
                    border: "none",
                    color: C.white,
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                  title="Click to remove"
                >
                  {opt}
                  <Icon name="x" size={10} color={C.white} />
                </button>
              ))}
              <button
                onClick={() => {
                  setEditingVariant(editing ? null : idx);
                  setVariantCustomInput("");
                }}
                style={{
                  padding: "6px 10px",
                  borderRadius: 20,
                  border: `1.5px solid ${C.hunter}`,
                  background: C.white,
                  color: C.hunter,
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                + Add
              </button>
            </div>

            {editing && (
              <div>
                {unselected.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                    {unselected.map((p) => (
                      <button
                        key={p}
                        onClick={() => addOption(idx, p)}
                        style={{
                          padding: "6px 10px",
                          borderRadius: 20,
                          border: `1.5px solid ${C.dustDk}`,
                          background: C.white,
                          color: C.muted,
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: "pointer",
                          fontFamily: "inherit",
                        }}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                )}
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    value={variantCustomInput}
                    onChange={(e) => setVariantCustomInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addOption(idx, variantCustomInput);
                        setVariantCustomInput("");
                      }
                    }}
                    placeholder="Type a custom option…"
                    style={{
                      flex: 1,
                      background: C.white,
                      borderRadius: 10,
                      padding: "10px 14px",
                      fontSize: 13,
                      fontFamily: "inherit",
                      color: C.charcoal,
                      border: "1px solid rgba(196,193,183,0.4)",
                      outline: "none",
                    }}
                  />
                  <button
                    onClick={() => {
                      addOption(idx, variantCustomInput);
                      setVariantCustomInput("");
                    }}
                    style={{
                      padding: "0 16px",
                      borderRadius: 10,
                      background: C.hunter,
                      border: "none",
                      color: C.white,
                      fontSize: 13,
                      fontWeight: 800,
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    Add
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {!showTypePicker ? (
        <button
          onClick={() => setShowTypePicker(true)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "10px 16px",
            borderRadius: 14,
            border: `1.5px dashed ${C.hunter}`,
            background: "transparent",
            color: C.hunter,
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer",
            fontFamily: "inherit",
            width: "100%",
            justifyContent: "center",
          }}
        >
          <Icon name="plus" size={14} color={C.hunter} />
          Add a variant type
        </button>
      ) : (
        <div style={{ background: C.cream, borderRadius: 14, padding: 12 }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: C.muted, marginBottom: 8 }}>
            Choose variant type
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {PRESET_VARIANT_TYPES.filter((t) => !variants.some((v) => v.type === t.label)).map((t) => (
              <button
                key={t.label}
                onClick={() => {
                  setVariants([...variants, { type: t.label, options: [] }]);
                  setEditingVariant(variants.length);
                  setVariantCustomInput("");
                  setShowTypePicker(false);
                }}
                style={{
                  padding: "10px 14px",
                  borderRadius: 10,
                  background: C.white,
                  border: `1.5px solid ${C.dustDk}`,
                  color: C.charcoal,
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                {t.icon} {t.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => setShowTypePicker(false)}
            style={{
              marginTop: 10,
              background: "transparent",
              border: "none",
              color: C.muted,
              fontSize: 12,
              cursor: "pointer",
              padding: 0,
              fontFamily: "inherit",
            }}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
